import { OPTION_CHAIN_HEAT_PALETTE } from './scales';

function hexToRgba(hex, alpha) {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const HEAT_POSITIVE_HEX = OPTION_CHAIN_HEAT_PALETTE.positive[OPTION_CHAIN_HEAT_PALETTE.positive.length - 1];
const HEAT_NEGATIVE_HEX = OPTION_CHAIN_HEAT_PALETTE.negative[OPTION_CHAIN_HEAT_PALETTE.negative.length - 1];

// Shared net-flow red/green endpoints so the histogram, the bottom net-volume
// map, and the heat cells all read as the same directional color system.
export const BOOKMAP_NET_FLOW_COLORS = {
  positiveHex: HEAT_POSITIVE_HEX,
  negativeHex: HEAT_NEGATIVE_HEX,
  positive: (alpha) => hexToRgba(HEAT_POSITIVE_HEX, alpha),
  negative: (alpha) => hexToRgba(HEAT_NEGATIVE_HEX, alpha),
};

// Dashboard-wide "current price / reference point" accent - deliberately
// distinct from the red/green net-flow system above (see
// docs/dashboard_visual_system.md: gold identifies brand, selection, or the
// current reference point, never direction).
export const PRICE_LINE_COLOR = '#f4c22b';

export const BOOKMAP_LC_VISUALS = {
  priceLineWidth: 1,
  mainGrid: {
    vertical: 'rgba(245, 248, 252, 0.07)',
    horizontal: 'rgba(245, 248, 252, 0.055)',
  },
  lowerGrid: {
    vertical: 'rgba(245, 248, 252, 0.12)',
    horizontal: 'rgba(245, 248, 252, 0.12)',
  },
  crosshair: {
    vertical: 'rgba(245, 248, 252, 0.5)',
    horizontal: 'rgba(245, 248, 252, 0.42)',
  },
  histogram: {
    left: 43,
    rightPad: 1,
    bottomPad: 34,

    // Histogram bar geometry
    barFillRatio: 0.42,
    barMinHeight: 2,
    barMaxHeight: 10,

    titleColor: 'rgba(245,248,252,0.9)',
    titleFont: "9px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
    tickFont: "11px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
    strikeFont: "11px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace",

    gridColor: 'rgba(245,248,252,0.2)',
    centerLineColor: 'rgba(245,248,252,0.58)',
    centerLineWidth: 1.1,

    positive: BOOKMAP_NET_FLOW_COLORS.positive(0.88),
    negative: BOOKMAP_NET_FLOW_COLORS.negative(0.92),
    positiveText: BOOKMAP_NET_FLOW_COLORS.positiveHex,
    negativeText: BOOKMAP_NET_FLOW_COLORS.negativeHex,
    neutralText: 'rgba(245,248,252,0.88)',
  },
};

export function lcGridColors(priceScaleVisible) {
  return priceScaleVisible ? BOOKMAP_LC_VISUALS.mainGrid : BOOKMAP_LC_VISUALS.lowerGrid;
}

export const BOOKMAP_STRIKE_RANGE = {
  halfSpan: 50,
  step: 5,
  minStrikes: 11,
  manualMinSpan: 0.01,
};

export const BOOKMAP_VERTICAL_DEADBAND = 0.65;
export const BOOKMAP_VERTICAL_EASE_MS = 250;

// Keep one strike row of padding at each fitted price-path edge. Bubble extent
// expands this range separately when a drawn radius needs more space.
export const PRICE_VISIBLE_WINDOW_PTS = BOOKMAP_STRIKE_RANGE.step;

// Widen a price-axis range symmetrically about its midpoint until it spans at
// least `minStrikes` rows. Applied as a floor on top of the data-driven fit, so
// wide-range days still widen past it.
export function expandBoundsToMinStrikes(bounds, config = BOOKMAP_STRIKE_RANGE) {
  if (!Number.isFinite(bounds?.from) || !Number.isFinite(bounds?.to)) return bounds;
  const step = config.step || 5;
  const minSpan = Math.max(0, (config.minStrikes ?? 1) - 1) * step;
  if (bounds.to - bounds.from >= minSpan) return bounds;
  const mid = (bounds.from + bounds.to) / 2;
  return { from: mid - minSpan / 2, to: mid + minSpan / 2 };
}

export function mergePriceBounds(...ranges) {
  const valid = ranges.filter((range) => (
    Number.isFinite(range?.from)
    && Number.isFinite(range?.to)
    && range.to >= range.from
  ));
  if (!valid.length) return null;
  return {
    from: Math.min(...valid.map((range) => range.from)),
    to: Math.max(...valid.map((range) => range.to)),
  };
}

export function meaningfulFlowStrikeBounds(snaps = [], isMeaningfulCell = null) {
  let from = Infinity;
  let to = -Infinity;
  for (const snap of snaps) {
    for (const [strikeKey, cell] of Object.entries(snap?.byStrike || {})) {
      const strike = Number(strikeKey);
      const meaningful = Number.isFinite(strike) && (
        typeof isMeaningfulCell === 'function'
          ? isMeaningfulCell(snap, strike, cell)
          : Object.values(cell || {})
            .some((value) => typeof value === 'number' && Number.isFinite(value) && value !== 0)
      );
      if (!meaningful) continue;
      from = Math.min(from, strike);
      to = Math.max(to, strike);
    }
  }
  return Number.isFinite(from) && Number.isFinite(to) ? { from, to } : null;
}

export function expandOnlyPriceBounds(previous, next) {
  return mergePriceBounds(previous, next);
}

export function flowWindowBounds(hydratedBounds, spot, windowPts) {
  const fallback = Number.isFinite(spot) && Number.isFinite(windowPts)
    ? { from: spot - windowPts, to: spot + windowPts }
    : null;
  if (!hydratedBounds) return fallback;
  if (!fallback) return hydratedBounds;
  const bounded = {
    from: Math.max(hydratedBounds.from, fallback.from),
    to: Math.min(hydratedBounds.to, fallback.to),
  };
  return bounded.to >= bounded.from ? bounded : null;
}

export function clampPriceBounds(bounds, ceiling) {
  if (!bounds || !ceiling) return bounds || null;
  const clamped = {
    from: Math.max(bounds.from, ceiling.from),
    to: Math.min(bounds.to, ceiling.to),
  };
  return clamped.to >= clamped.from ? clamped : null;
}

export function clampManualPriceBounds(bounds, ceiling, config = BOOKMAP_STRIKE_RANGE) {
  if (!Number.isFinite(bounds?.from) || !Number.isFinite(bounds?.to) || !(bounds.to > bounds.from)) {
    return null;
  }
  const minSpan = Number(config.manualMinSpan ?? 0.01);
  if (!Number.isFinite(minSpan) || minSpan <= 0) return null;
  const span = Math.max(bounds.to - bounds.from, minSpan);
  const midpoint = (bounds.from + bounds.to) / 2;
  let next = { from: midpoint - span / 2, to: midpoint + span / 2 };

  if (!Number.isFinite(ceiling?.from) || !Number.isFinite(ceiling?.to)) return next;
  const ceilingSpan = ceiling.to - ceiling.from;
  if (ceilingSpan < minSpan) return null;
  if (span >= ceilingSpan) return { from: ceiling.from, to: ceiling.to };
  if (next.from < ceiling.from) next = { from: ceiling.from, to: ceiling.from + span };
  if (next.to > ceiling.to) next = { from: ceiling.to - span, to: ceiling.to };
  return next;
}

export function recenterSpotRange(
  current,
  spot,
  span = BOOKMAP_STRIKE_RANGE.halfSpan * 2,
  deadband = BOOKMAP_VERTICAL_DEADBAND,
  minSpan = (BOOKMAP_STRIKE_RANGE.minStrikes - 1) * BOOKMAP_STRIKE_RANGE.step
) {
  if (!Number.isFinite(spot) || !Number.isFinite(span) || span <= 0) return current || null;
  const height = Math.max(span, minSpan);
  if (current && Number.isFinite(current.from) && Number.isFinite(current.to)) {
    const margin = height * (1 - deadband) / 2;
    if (spot >= current.from + margin && spot <= current.to - margin) return current;
  }
  return { from: spot - height / 2, to: spot + height / 2 };
}

// Pure spot-centered strike grid: every `step` from spot-halfSpan to spot+halfSpan,
// snapped to the step. Shared by Bookmap Pro/Sandbox, the LC variants, and Flow Book
// so the vertical strike count is identical across tabs regardless of intraday range.
// Returns ascending rows; callers reverse for top-down rendering.
export function spotStrikeRows(spot, config = BOOKMAP_STRIKE_RANGE) {
  if (!Number.isFinite(spot)) return [];
  const step = config.step || 5;
  const mid = Math.round(spot / step) * step;
  const rows = [];
  for (let strike = mid - config.halfSpan; strike <= mid + config.halfSpan; strike += step) {
    rows.push(strike);
  }
  return rows;
}
